import { queryOptions, useQuery } from '@tanstack/react-query';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { EnvironmentVariables } from '../EnvironmentVariables';
import { useEffect } from 'react';

export enum EHttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE'
}

export const usePortalQuery = <T>(
  method: EHttpMethod,
  path: string,
  queryIdentifier: string,
  enabled: boolean,
  data?: any,
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
  refetchOnMount = true,
  staleTime = 0,
  cacheTime = 0
) => {
  const basePath = EnvironmentVariables.ApiUrl;
  const isMutation =
    method === EHttpMethod.POST || method === EHttpMethod.PUT || method === EHttpMethod.DELETE;

  const handleAxiosErrorResponse = (error: AxiosError) => {
    if ([401, 403].includes(error.response?.status ?? 0)) {
      window.location.href = EnvironmentVariables.AuthUrl;
    } else if (error.code == 'ERR_BAD_REQUEST' && error.response == undefined) {
      throw { msg: 'The server was unable to respond', code: error.status };
    } else {
      throw error.response?.data;
    }
  };

  const getQueryFunction: (method: EHttpMethod, path: string) => () => any = (
    method: EHttpMethod,
    path: string
  ) => {
    let query: (url: string, body?: any, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
    const args: [string, any?] = [`${basePath}/${path}`];
    if (isMutation) {
      args.push(data);
    }
    switch (method) {
      case EHttpMethod.GET:
        query = axios.get<T>;
        break;
      case EHttpMethod.PUT:
        query = axios.put<T>;
        break;
      case EHttpMethod.POST:
        query = axios.post<T>;
        break;
      case EHttpMethod.DELETE:
        query = axios.delete<T>;
        break;
    }
    return () => {
      console.log(`Querying ${method}: ${path}`);
      return query(...args, { withCredentials: true })
        .then(response => response.data)
        .catch((err: AxiosError) => {
          handleAxiosErrorResponse(err);
        });
    };
  };

  const response = useQuery<T>(
    queryOptions<T>({
      queryKey: [queryIdentifier],
      queryFn: getQueryFunction(method, path),
      retry: isMutation ? false : (err: any) => err.code === 404,
      refetchOnMount: refetchOnMount && !isMutation,
      refetchOnWindowFocus: false,
      refetchOnReconnect: !isMutation,
      gcTime: cacheTime,
      staleTime,
      enabled
    })
  );

  useEffect(() => {
    if (response.isError) {
      onError && onError(response.error);
    }
    if (response.isSuccess) {
      onSuccess && onSuccess(response.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.data, response.isError]);

  return response;
};
